export const STRINGS = {
  dismiss: 'Dismiss',
  loading: 'Loading',

  /** Web tab title + default native stack titles (React Navigation sets `document.title` on web). */
  appDocumentTitle: 'U.S. Kids Golf, LLC',

  supportEmail: 'coach@uskidsgolf.com',

  smartAppBanner: {
    dismissAccessibilityLabel: 'Dismiss app banner',
    openAccessibilityLabel: 'Open app',
    title: 'U.S. Kids Golf Player Pathway',
    publisher: 'U.S. Kids Golf',
    caption: 'FREE - Download now',
    open: 'Open',
    get: 'Get',
  },

  // Guardian onboarding wizard (4 steps). Only step 1 is designed in Figma so far;
  // steps 2–4 are placeholders until their designs land.
  onboarding: {
    skip: 'Skip',
    buttonContinue: 'Continue',
    buttonBack: 'Back',
    step1: {
      title: 'Welcome, {name} 👋🏻',
      subtitle: 'A few quick steps so we know who to track and where you play.',
      labelFirstName: 'First Name',
      placeholderFirstName: 'Enter first name',
      labelLastName: 'Last Name',
      placeholderLastName: 'Enter last name',
      sectionOther: 'Other Information',
      labelPhone: 'Phone Number',
      placeholderPhone: '+1 (123) 456-7890',
      labelAltPhone: 'Alt Phone Number',
      placeholderAltPhone: '+1',
      labelEmail: 'Email',
      placeholderEmail: 'Enter your email',
      labelCountry: 'Country',
      placeholderCountry: 'Select country',
      labelCity: 'City',
      placeholderCity: 'Enter city',
      labelState: 'State/Province',
      placeholderState: 'Select state/province',
      labelZip: 'Zip Code',
      placeholderZip: 'Enter zip code',
      notice:
        'We’ll match your email to existing player profiles\nIf a coach already added your players, you’ll see them next.',
    },
    step2: {
      title: 'Manage your players',
      subtitle:
        'We found some players linked to your email\n{email}.\nYou can add another player yourself.',
      noPlayerSubtitle:
        'We couldn’t find any players linked to your email\n{email}.\nYou can add a player to get started.',
      matched: (count: number) =>
        `${count} ${count === 1 ? 'player' : 'players'} matched to your account`,
      levelBadge: 'LEVEL {level}',
      newBadge: 'New',
      addPlayer: 'Add player',
    },
    addPlayer: {
      title: 'Add player',
      editTitle: 'Edit player',
      viewTitle: 'View player',
      deletePlayer: 'Delete player',
      close: 'Close',
      // Tablet popup footer actions (the phone/landscape sheet submits via the header check icon).
      cancel: 'Cancel',
      create: 'Create',
      save: 'Save',
      labelFirstName: 'First Name',
      placeholderFirstName: 'Enter first name',
      labelLastName: 'Last Name',
      placeholderLastName: 'Enter last name',
      labelBirthDate: 'Birth Date',
      birthDateSheetTitle: 'Date of Birth',
      placeholderBirthDate: 'Select date',
      labelGender: 'Gender',
      genderPlaceholder: 'Select gender',
      genderUnspecified: 'Unspecified',
      genderMale: 'Male',
      genderFemale: 'Female',
      labelLevel: 'Level',
      levelValue: 'Level {level}',
      labelHeight: 'Height',
      placeholderHeight: '48"',
      labelNeedClub: 'Need Club',
      needClubYes: 'Yes',
      needClubNo: 'No',
      labelDominantHand: 'Dominant Hand',
      handRight: 'Right',
      handLeft: 'Left',
      sectionAddress: 'Address',
      sameAddress: 'Same address with me',
      labelCountry: 'Country',
      placeholderCountry: 'Select country',
      labelCity: 'City',
      placeholderCity: 'Enter city',
      labelState: 'State/Province',
      placeholderState: 'Select state/province',
      labelZip: 'Zip Code',
      placeholderZip: 'Enter zip code',
      // Guardian "Add Player" screen (PP-376) — full-screen create from the Homepage.
      screenTitle: 'Add Player',
      editScreenTitle: 'Edit Player',
      saveButton: 'Save',
      labelStateProvince: 'State/Province',
      placeholderStateProvinceSelect: 'Select State/Province',
      placeholderStateProvinceText: 'Enter State/Province',
      placeholderStateProvinceNoCountry: '--',
      deleteConfirmTitle: 'Delete Player',
      deleteConfirmMessage:
        'Are you sure you want to delete this player? All information about this player will be deleted.',
      deleteConfirm: 'Delete',
      deleteCancel: 'Cancel',
      deleteErrorTitle: 'Delete failed',
      deleteErrorMessage: 'Something went wrong while deleting. Please try again.',
    },
    step3: {
      title: 'Find a nearby coach',
      subtitle: "We'll suggest classes from your chosen coach.",
      searchPlaceholder: 'Search by Coach Name',
      mapSearchPlaceholder: 'Address or zip code',
      nearest: 'Nearest',
      /** Toggle above the Nearest list; when on, each item also shows its full address. */
      compactView: 'Compact view',
      /** "{distance} • {coaches} coaches" */
      meta: '{distance} • {coaches} coaches',
      /** Shown in place of the Nearest list when location permission is denied/unavailable. */
      noLocationTitle: "We're unable to find your location",
      noLocationSubtitle: 'Please enter your zip code to find a coach near you.',
      /** Shown when we have a location but no coach matches the current search/area. */
      notFoundTitle: 'Not found',
      notFoundSubtitle: 'No results match your search criteria',
    },
    step4: {
      title: 'Stay in the loop',
      subtitle:
        "Pick what's worth a notification & enable ${kind} for faster sign-in. Change anytime in Settings.",
      subtitleWithoutBiometrics: "Pick what's worth a notification. Change anytime in Settings.",
      finish: 'Finish',
      warning: "Turn on app notifications so you don't miss any important updates.",
      classRemindersTitle: 'Class Reminders',
      classRemindersSubtitle: '1 hour before each lesson',
      coachNotesTitle: 'New Pins & Coach Notes',
      coachNotesSubtitle: 'After every session',
      tournamentUpdatesTitle: 'Tournament Updates',
      tournamentUpdatesSubtitle: 'Reminders, waitlist, results',
      coGuardianActivityTitle: 'Co-guardian Activities',
      coGuardianActivitySubtitle: 'When other guardians book',
      shopDealsTitle: 'Shop & Deals',
      shopDealsSubtitle: 'recommended gear for your kid',
      faceIdTitle: 'Login with ${kind}',
      faceIdSubtitle: 'Use ${kind} to log in instead of a password.',
      faceIdPrompt: 'Confirm your identity to enable biometric login',
      biometricLockout:
        'Too many failed attempts. Biometric authentication is temporarily locked. Please try again later.',
      biometricNotSupported: 'This device does not support biometrics authentication.',
      biometricsDisabled: 'Biometrics Disabled',
      biometricsDisabledDesc: `Using Biometrics instead of a password makes signing in faster and more secure. You can go to App Settings > Biometrics to enable this feature.`,
    },
    // Confirmation screen shown once the wizard finishes (or all steps are skipped).
    finish: {
      title: "You're all set!",
      subtitle: 'Your account is ready. Add your player to start tracking their golf journey.',
      myKids: 'My Players',
      addChildTitle: 'Add your player',
      addChildSubtitle:
        'Link a player to see their practice sessions, levels and tournaments here.',
      upcoming: 'Upcoming',
      emptyTitle: 'Nothing scheduled yet',
      emptySubtitle: 'Add your player above and their upcoming sessions will appear here.',
      inviteText:
        'Want someone else to see this too? Invite a Co-guardian - They get the same view.',
      invite: 'Invite',
      takeMeHome: 'Take me home',
    },
  },

  errors: {
    firestoreTimeout: 'Something went wrong. Please try again.',
    unexpectedErrorTitle: 'Something went wrong',
    unexpectedError:
      'The app encountered an unexpected error. Please try again, or contact support if the problem persists.',
    requestTimeoutTitle: 'Request Time-out',
    requestTimeoutMessage: 'Something went wrong. Please try again.',
    noInternetTitle: 'No internet connection',
    noInternetMessage: 'Check your internet connection and try again.',
    ok: 'OK',
  },

  /** Shared copy: tabs, reusable alerts, component fallbacks */
  common: {
    noPhone: 'No Phone',
    noAddress: 'No Address',
    /** Default title for the phone-input country picker sheet. */
    countryPickerTitle: 'Select Country Code',
    /** Searchable Country/Region address picker. */
    countryRegionPicker: {
      title: 'Select Country/Region',
      placeholder: 'Select country',
      searchPlaceholder: 'Enter Country Name',
      selectedSection: 'Selected',
      noResults: 'No results',
    },
    /** State/Province field placeholders (combobox when the country has a list, else free text). */
    stateProvince: {
      selectPlaceholder: 'Select State/Province',
      enterPlaceholder: 'Enter State/Province Name',
      noCountryPlaceholder: '--',
    },
    navigation: {
      tabDashboard: 'Dashboard',
      tabPlayers: 'Players',
      tabPlayerPathway: 'Player Pathway',
      tabNotifications: 'Notifications',
    },
    noticeTitle: 'Notice',
    webNotificationsDeniedMessage: 'Enable notifications in your browser settings.',
    return: 'RETURN',
    buttons: {
      back: 'Back',
      continue: 'Continue',
      dismiss: 'Dismiss',
    },
    playerPathwayScreen: {
      title: 'Player Pathway',
      signOut: 'Sign Out',
      buttons: {
        levels1to3: 'Levels 1-3',
        levels4to6: 'Levels 4-5',
        levels6to8: 'Levels 6-8',
        levels9to10: 'Levels 9-10',
      },
      noteRevision: 'Levels 6-10: ',
      noteRevision1: 'In revision.',
      sheet: {
        back: 'Back',
      },
      download: {
        errorTitle: 'Notice',
        errorMessage: 'Unable to download the file. Please try again.',
      },
    },
    alert: {
      info: 'Info',
      error: 'Error',
      success: 'Success',
      confirmTitle: 'Confirm',
      confirm: 'Confirm',
      cancel: 'Cancel',
    },
    selectDefaultPlaceholder: 'Select',
    imageRenderError: 'Image render error',
    featureUnderProgress: 'This feature is under progress.',
    viewMore: 'View more',
    showLess: 'Show less',
    defaultScreen: {
      title: 'Feature is in development',
      subtitle: 'COMING SOON',
    },
    defaultCancel: {
      noticeTitle: 'Notice',
      dismiss: 'Dismiss',
      cancelMessage: 'Any changes you have made will be lost.',
      confirmDiscard: 'Continue',
      continueEditing: 'Cancel',
    },
    photoPermissionAlert: {
      title: 'Allow Access to Your Photos',
      message:
        'This lets you share from your camera roll, and enables other features for photos. Go to Settings and tap "Photos".',
      settings: 'Settings',
      notNow: 'Not now',
    },
    locationPermissionAlert: {
      title: 'Allow Access to Your Location',
      message:
        'Your location will be used to show you nearby places, traffic updates, and find your location.',
      settings: 'Settings',
      notNow: 'Not now',
    },
  },

  auth: {
    welcome: {
      tagline: 'Track every pin, every session,\nevery tournament - across, every guardian.',
      buttonCreateAccount: 'Create Account',
      buttonLogin: 'Login',
      termsPrefix: 'By creating an account you are agreeing to our ',
      termsOfService: 'Terms of Service',
      termsConjunction: ' and ',
      privacyPolicy: 'Privacy Policy.',
    },
    // Titles for the Terms of Service / Privacy Policy popup (shared across auth screens).
    legal: {
      termsTitle: 'Terms of Service',
      privacyTitle: 'Privacy Policy',
      closeAccessibilityLabel: 'Close',
    },
    signUp: {
      title: 'Create account',
      subtitle: 'So you can track your players’ golf journey',
      labelFirstName: 'First Name',
      labelLastName: 'Last Name',
      labelPhone: 'Phone Number',
      labelEmail: 'Email',
      labelPassword: 'Password',
      placeholderFirstName: 'Enter First Name',
      placeholderLastName: 'Enter Last Name',
      placeholderPhone: '+1',
      placeholderEmail: 'Enter your email',
      placeholderPassword: 'Enter your password',
      buttonCreateAccount: 'Create account',
      orLoginWith: 'or login with',
      termsPrefix: 'By creating an account you are agreeing to our ',
      termsOfService: 'Terms of Service',
      termsConjunction: ' and ',
      privacyPolicy: 'Privacy Policy.',
      requiredFirstName: 'This field is required.',
      requiredLastName: 'This field is required.',
      requiredPhone: 'This field is required.',
      invalidPhone: 'Phone Number must be at least 10 characters.',
      requiredEmail: 'This field is required.',
      invalidEmail: 'Invalid Email format.',
      requiredPassword: 'This field is required.',
      invalidPassword: 'Password must be at least 8 characters.',
      requiredTerms: 'Please agree to the Terms of Service and Privacy Policy to continue.',
      // Error popups when the email is already registered (PP-281 scenarios 3/4/5).
      errorEmailInUseTitle: 'Email already in use',
      errorEmailInUseMessage:
        'An account with this email already exists. Please log in to continue.',
      errorEmailNotVerifiedTitle: 'Email not verified yet',
      errorEmailNotVerifiedMessage:
        "This email is already registered but hasn't been verified.\nPlease login to continue.",
      errorSocialAccountTitle: 'Account already exists',
      // `{provider}` is replaced with Google or Apple at runtime.
      errorSocialAccountMessage:
        'This email is linked to an existing account. Please log in with {provider} to continue.',
      buttonLogin: 'Log in',
      buttonVerifyNow: 'Verify now',
      buttonLater: 'Later',
    },
    login: {
      title: 'Login to your account',
      labelEmail: 'Email',
      labelPassword: 'Password',
      placeholderEmail: 'Enter your email',
      placeholderPassword: 'Enter your password',
      validationPassword: 'This field is required.',
      forgotPassword: 'Forgot Password?',
      buttonLogin: 'Login',
      orLoginWith: 'or login with',
      termsPrefix: 'By creating an account you are agreeing to our ',
      termsOfService: 'Terms of Service',
      termsConjunction: ' and ',
      privacyPolicy: 'Privacy Policy.',
      tooManyRequest: 'Too many requests. Please wait before trying again!',
    },
    loginWithBiometrics: {
      /** Greeting above the password field. `{name}` is the signed-in user's first name. */
      greeting: 'Hi, {name}',
      greetingFallback: 'Welcome back',
      logoutAccessibilityLabel: 'Log out of this account',
      labelPassword: 'Password',
      placeholderPassword: 'Enter your password',
      forgotPassword: 'Forgot Password?',
      buttonLogin: 'Login',
      biometricAccessibilityLabel: 'Login with biometrics',
      biometricPrompt: 'Confirm your identity to log in',
      biometricNotEnabledTitle: 'Biometric Login Not Enabled',
      biometricNotEnabledMessage:
        'Please log in to your account first, then go to Profile > Account Securities to enable Biometric Login.',
      orLoginWith: 'or login with',
      termsPrefix: 'By creating an account you are agreeing to our ',
      termsOfService: 'Terms of Service',
      termsConjunction: ' and ',
      privacyPolicy: 'Privacy Policy.',
    },
    enterEmail: {
      placeholderEmail: 'Enter your email',
      buttonNext: 'Next',
      wrongCredentialTitle: 'Notice',
      wrongCredentialDescription: 'Incorrect email or password. Please try again.',

      alertTitle: 'Alert!',
      alertEmailNotApproved:
        'It looks like this email address has not been approved to use the Player Pathway yet. Please contact coach@uskidsgolf.com to check your status.',
      alertEmailNotVerified: 'The email address you entered is not valid. Please try again.',
      noticeTitle: 'Notice',
      errorCheckEmail: 'Please check your email',
      errorEmailNotRegist: 'This email hasn’t been registered yet. Please try again.',
      validationEmail: 'Please enter an email address verified by U.S. Kids Golf.',
      requiredEmail: 'This field is required.',
      invalideEmail: 'Invalid Email format.',
      environmentPlaceholder: 'Select environment',
      environmentDevelopment: 'Develop',
      environmentProduction: 'Product',
    },
    enterPassword: {
      placeholderPassword: 'Enter password',
      buttonSignIn: 'Sign In',
      buttonForgotPassword: 'Forgot Password',
      buttonBack: 'Back',
      noticeTitle: 'Notice',
      errorPassword: 'Incorrect email or password. Please try again.',
      errorCurrentPassword: 'Incorrect current password. Please try again.',
      errorSignIn:
        'Something went wrong while signing you in. Please try again or contact support if the issue persists.',
    },
    forgotPassword: {
      title: 'Forgot Your Password?',
      description: 'Provide the email associated to your\nProgress Tracker Account:',
      validationEmail: 'Please enter a valid email.',
      placeholderEmail: 'Enter your email',
      buttonSendResetEmail: 'Send Reset Email',
      buttonCancel: 'Cancel',
      noticeTitle: 'Notice',
      alertTitle: 'Alert!',
      successMessage: 'Please check your email for next steps.',
      errorSendResetEmail: 'Something went wrong while sending the reset email. Please try again.',
      resentSuccess: 'Another reset password email has been sent!',
    },
    forgotPasswordV1: {
      title: 'Forgot your password?',
      description: 'Enter your email address and we will send a code to reset your password.',
      labelEmail: 'Email',
      placeholderEmail: 'Enter your email',
      buttonContinue: 'Continue',
      backToLogin: 'Back to Login',
    },
    forgotPasswordLinkSent: {
      title: 'Check your inbox',
      descriptionPrefix: "We've sent a password reset link to",
      descriptionFollow: 'Follow the instructions to set a new password.',
      descriptionSpam: "If you don't see it, check your spam or junk folder.",
      resendPrompt: "Didn't receive the email? ",
      resend: 'Resend',
      buttonBackToLogin: 'Back to login',
    },
    verifyEmailLinkSent: {
      title: 'Verify your email',
      descriptionPrefix: "We've sent a verification email to",
      descriptionFollow:
        "Please follow the instructions to activate your account and you'll be ready to use the app.",
      buttonBackToLogin: 'Back to login',
    },
    verifyEmail: {
      title: 'Verify Your Email',
      description: 'Your email is not yet verified',
      buttonSendVerificationEmail: 'Send Verification Email',
      buttonCancel: 'Cancel',
      noticeTitle: 'Notice',
      successMessage:
        'We have sent a verification link to your email address.\nAfter verification, you can reset the password to start using the app.',
      errorSendVerificationEmail:
        'Something went wrong while sending the verification email. Please try again.',
    },
    loginCredentials: {
      title: 'Login Credentials',
      labelEmail: 'Email',
      labelPassword: 'Password',
      /** Visual mask only (not the real password length). */
      maskedPassword: '********',
      emailUnavailable: '—',
      buttonUpdatePassword: 'Update Password',
    },
    changePassword: {
      title: 'Change Password',
      sectionTitle: 'Setup Your Password',
      sectionDescription: 'This password is required to log into your Player Pathway app.',
      placeholderCurrent: 'Enter your current password',
      placeholderNew: 'Create a new password',
      placeholderConfirm: 'Confirm your new password',
      buttonUpdate: 'Update Password',
      buttonCancel: 'Cancel',
      successMessage: 'Your password has been changed!',
      cancelConfirmMessage: 'Any changes you have made will be lost.',
      validationMinLength: 'Password must include at least 8 characters',
      validationConfirmMismatch: 'Confirmation password does not match',
      errorSessionExpired: 'Session expired. Please sign in again.',
    },
    sessionTimeout: {
      title: 'Session Timeout',
      message: 'Your session has expired. Please log in again to continue using the app.',
      signIn: 'Sign In',
    },
    // OIDC SSO interaction UI (web). Consent prompt shown when a partner requests the
    // `players` scope (the guardian's children list — child data); the guardian must approve
    // sharing it explicitly. `sharedItems` mirrors the BE `players` claim shape (id + name +
    // avatar only — see guardian-view-be players-claim.ts).
    sso: {
      playersConsent: {
        title: "Share your children's information?",
        description:
          "To continue, this partner is asking to access your children's information from your U.S. Kids Golf account:",
        sharedItems: ["Your children's names", 'Their profile photos'],
        note: "You can decline and still sign in — your children's information just won't be shared.",
        allow: 'Allow',
        decline: 'Not now',
      },
      error: 'Sign-in could not be completed. Please close this window and try again.',
      completing: 'Signing you in…',
      confirmContinueWithEmail: 'Continue with email {email}',
      confirmContinue: 'Continue signing in?',
      confirmContinueButton: 'Continue',
      confirmCancelButton: 'Cancel',
    },
  },

  drawer: {
    close: 'CLOSE',
    role: 'Coach',
    supportEmail: 'coaches@uskidsgolf.com',
    menu: {
      myCoachProfile: 'My Coach Profile',
      loginCredentials: 'Login Credentials',
      support: 'Support',
      switchRole: 'Switch Role',
      logout: 'Logout',
    },
  },

  home: {
    header: {
      coachLabel: 'Coach',
    },
    greeting: {
      morning: 'Good morning',
      afternoon: 'Good afternoon',
      evening: 'Good evening',
    },
    playerPathway: {
      body: 'Player Pathway Progress Tracker',
      buttonTrack: 'Track Progress Now',
      buttonResume: 'Resume Tracking Progress',
    },
    guardian: {
      myKids: 'My Players',
      addPlayerEmptyCard: {
        title: 'Add a player',
        subtitle: 'No golfers yet — add one to see their schedule and progress here.',
      },
      getStarted: {
        title: 'Get Started',
        steps: {
          step1: {
            title: 'Add a Player',
            subtitle: 'Name, age & home academy',
          },
          step2: {
            title: 'Book a first lesson',
            subtitle: 'Unlocks after you add a player',
          },
          step3: {
            title: 'Track progress & pins',
            subtitle: 'See levels on the Player Pathway',
          },
        },
      },
    },
  },

  player: {
    title: 'Player',
    profile: {
      ageYearOld: 'year old',
      ageYearsOld: 'years old',
      header: {
        back: 'BACK',
        title: 'PLAYER PROFILE',
        edit: 'EDIT',
      },
      tabs: {
        overview: 'Overview',
        progressTracker: 'Progress Tracker',
        notes: 'Notes',
        equipment: 'Equipment',
      },
      body: {
        progressTracker: 'Progress Tracker',
        notes: 'Notes',
        equipment: 'Equipment',
      },
      progressTrackerContent: {
        currentLevelTitle: 'CURRENT LEVEL',
        completedLevelsTitle: 'COMPLETED LEVELS',
        details: 'DETAILS',
        levelDetailsTitle: 'LEVEL DETAILS',
        lastClassAttendedLabel: 'Last Class Attended:',
        masteredLabel: 'Mastered',
        onLabel: 'On',
        masteredOnLabel: 'Mastered On',
        statusComplete: 'Complete',
        statusNotComplete: 'Not Complete',
        completedOnLabel: 'Completed on',
        incompleteAttemptOnLabel: 'Incomplete Attempt on',
        completed: 'Completed',
        incomplete: 'Incomplete',
        na: 'N/A',
        badgeTitles: {
          aroundTheGreen: 'Around The Green',
          fullSwing: 'Full Swing',
          scoring: 'Scoring',
          atg: 'ATG',
        },
        checkpointTitles: {
          forms: 'Forms',
          compass: 'Compass',
          combine: 'Combine',
        },
      },
      notesContent: {
        addNewNote: 'ADD NEW NOTE',
        details: 'DETAILS',
        detailsSheetTitle: 'NOTE DETAILS',
        newNoteTitle: 'NEW NOTE',
        saveSuccessMessage: 'Note added successfully!',
        alertTitle: 'Alert!',
        blankFieldsMessage: 'Information should not be blank.',
        titlePlaceholder: 'Note Title',
        detailPlaceholder: '',
        save: 'Save',
        cancel: 'Cancel',
        continue: 'Continue',
        saveProgressTitle: 'Save Progress',
        noticeTitle: 'Notice',
        saveProgressMessage: 'You are about to save this note. Are you sure you want to save it?',
        noticeMessage: 'Any changes you have made will be lost.',
        saveErrorTitle: 'Error',
        saveErrorMessage: 'Something went wrong while saving your note. Please try again.',
        saving: 'SAVING...',
      },
      overview: {
        actions: {
          call: 'Call',
          text: 'Text',
          email: 'Email',
        },
        contactInfoTitle: 'CONTACT INFO',
        labels: {
          parentName: 'PARENT NAME',
          phoneNumber: 'PHONE NUMBER',
          email: 'EMAIL',
        },
      },
      equipment: {
        title: 'Player Pathway Kit',
        showAll: 'Show All',
        add: 'ADD',
        addNewKit: 'Add new kit',
        sheetTitle: 'PLAYER PATHWAY KIT',
        addSheetTitle: 'ADD A NEW KIT',
        golfClubInfoTitle: 'Golf club info',
        details: 'DETAILS',
        kitLevelLabel: 'Kit Level:',
        kitLevelPlaceholder: 'Select Level',
        dateProvidedLabel: 'Date Provided:',
        saveChanges: 'SAVE CHANGES',
        noKits: 'No kits found',
        cancel: 'CANCEL',
        addKitSuccessMessage: 'A new KIT has been added successfully',
        kitLevel: (level: number) => `LEVEL ${level}`,
        by: 'by',
      },
    },
    filters: {
      back: 'Back',
      buttonDefault: 'Filter',
      buttonWithLevel: (level: number) => `: Level ${level}`,
      sheetTitle: 'Filter Results',
      sectionByLevel: 'By Level',
      sectionByLastActive: 'By Last Active',
      levelLabel: (level: number) => `Level ${level}`,
      lastActive: {
        oneMonth: '1 Month',
        twoMonths: '2 Months',
        threeMonths: '3 Months',
        sixMonths: '6 Months',
        oneHalfYears: '1.5 Years',
        showAll: 'Show All',
        noInactiveFilter: 'No Inactive Filter',
      },
      done: 'DONE',
    },
    sort: {
      back: 'Back',
      sheetTitle: 'Sort Results',
      sectionByFirstName: 'By First Name',
      sectionByLevel: 'By Level',
      sectionByAge: 'By Age',
      ascending: 'Ascending order',
      descending: 'Descending order',
      done: 'DONE',
      buttonName: 'Sort: Name',
      buttonLevel: 'Sort: Level',
      buttonAge: 'Sort: Age',
      buttonDefault: 'Sort',
    },
    listPlayers: {
      title: 'Search players',
      createProfileButton: 'Create a new profile',
      searchPlaceholder: 'Search for a player',
      filterButton: 'Filter',
      sortButton: 'Sort: Name',
      resultsSummaryTitleTablet: 'View Player Profile',
      emptyListMessage: 'No players found',
      loadingPlayers: 'Loading players...',
      // Marker may be appended by the backend while a player is being created.
      creatingPlayerMarker: '(CREATING PLAYER)',
      /** e.g. "5 Years Old" / "1 Year Old" — age number rendered separately */
      ageYearOld: 'Year Old',
      ageYearsOld: 'Years Old',
      resultsSummaryShowAll: (start: number, end: number, total: number) =>
        `${start}-${end} of ${total} ${total === 1 ? 'Result' : 'Results'}`,
      resultsSummaryWithLastActive: (
        start: number,
        end: number,
        total: number,
        lastActiveLabel: string,
      ) =>
        `${start}-${end} of ${total} ${
          total === 1 ? 'Result' : 'Results'
        } Active in Last ${lastActiveLabel}`,
    },
    mutatePlayer: {
      header: {
        back: 'BACK',
        title: 'CREATE A PLAYER',
        editTitle: 'EDIT PLAYER',
      },
      levelTab: {
        tabProfile: 'Profile',
        tabLevel: 'Level',
        changeCurrentLevelTitle: 'CHANGE THE CURRENT PLAYER LEVEL',
        edit: 'EDIT',
        done: 'DONE',
        current: 'CURRENT',
        statusCompleted: 'COMPLETED',
        statusIncomplete: 'INCOMPLETE',
        /** Inline label next to level/skill rows (iOS-style). */
        statusPartial: 'PARTIAL',
        statusPartialCompletion: 'PARTIAL',
        clearData: `Clear data`,
        levelChangedHint:
          'You changed the current player level. Save these changes to continue to delete specific pathway data.',
        removeProgressTitle: 'Remove Progress',
        removeProgressMessage: (playerName: string) =>
          `You are about to permanently remove progress for ${playerName}. Would you like to proceed?`,
        removeProgressNo: 'No',
        removeProgressYes: 'Yes',
        removeProgressPlayerFallback: 'this player',
      },
      profile: {
        uploadPhoto: 'UPLOAD PHOTO',
        uploadPhotoHint: 'Image should not exceed 10MB',
        uploadPhotoUnsupportedTitle: 'Alert!',
        uploadPhotoUnsupportedMessage: (objectType: string) =>
          `${objectType} format is not supported`,
        uploadPhotoUnreadableMessage: 'Unable to load this image. Please try a different photo.',
        firstName: 'First Name',
        lastName: 'Last Name',
        birthDate: 'Birth Date',
        birthDatePlaceholder: '00/00/00',
        playerHeight: 'Player Height',
        playerHeightPlaceholder: '48"',
        playerGender: 'Player Gender',
        playerGenderPlaceholder: 'Select gender',
        genderMale: 'Male',
        genderFemale: 'Female',
        level: 'Level',
        levelPlaceholder: 'Level 1',
        levelLabel: (level: number) => `Level ${level}`,
      },
      contact: {
        title: 'CONTACT INFO',
        parentFirstName: 'Parent First Name',
        parentLastName: 'Parent Last Name',
        phoneNumber: 'Phone Number',
        altPhoneNumber: 'Alt Phone Number',
        phonePlaceholder: '+1',
        email: 'Email',
        country: 'Country',
        city: 'City',
        state: 'State/Province',
        zipCode: 'Zip Code',
      },
      club: {
        title: 'CLUB INFO',
        needClubs: 'NEED CLUBS?',
        needClub: 'NEED CLUBS',
        dominantHand: 'DOMINANT HAND',
        yes: 'Yes',
        no: 'No',
        left: 'Left',
        right: 'Right',
      },
      footer: {
        saveChanges: 'SAVE CHANGES',
        save: 'SAVE',
        cancel: 'Cancel',
        deletePlayer: 'Delete Player',
        deleteConfirmMessage: 'Are you sure you want to delete this player?',
        deleteConfirmText: 'Delete',
        deleteWarningTitle: 'Warning',
        deleteWarningMessage: 'You have selected to delete this player. Are you sure?',
        playerRemovedTitle: 'Player Removed',
        playerRemovedMessage:
          'You have successfully deleted this player. The account can be restore by contacting your system admin.',
        return: 'Return',
        creatingLoading: 'Creating player...',
        updatingLoading: 'Updating player...',
        deletingLoading: 'Deleting player...',
        updateSuccessMessage:
          'Account details updated successfully! Changes will reflect momentarily.',
      },
    },
    profileLabel: 'Profile',
  },

  playerSession: {
    headerOverviewSession: {
      back: 'BACK',
      title: 'ACTIVE SESSION',
      end: 'END',
      trackingPlayers: (count: number) => `TRACKING ${count} PLAYER(S)`,
      noPlayers: 'No results found',
    },
    selectPlayers: {
      headerTitle: 'SELECT PLAYERS',
      back: 'BACK',
    },
    actions: {
      add: 'ADD',
      new: 'NEW',
      start: 'START',
      resume: 'RESUME',
      trackingSummary: (count: number) => `TRACKING ${count} PLAYER(S)`,
      selectedSummaryCount: (count: number) => `${count} PLAYER(S) SELECTED`,
    },
    sessionSummary: {
      title: 'SESSION SUMMARY',
      close: 'CLOSE',
      details: 'DETAILS',
      overview: 'OVERVIEW',
      emptyMessage: 'No attempts were made during this session.',
      noPinsMessage: 'No pins were earned during this session.',
    },
    stepDetail: {
      complete: 'COMPLETE',
      incomplete: 'INCOMPLETE',
      tryAgain: 'TRY AGAIN',
      skip: 'SKIP',
      unset: 'Unset',
      unsetAlertTitle: 'Alert!',
      unsetAlertBody:
        'You have attempted to unset player progress that has been saved from a previous session. Are you sure would like to proceed?',
      cancel: 'Cancel',
      skill1Label: 'Skill 1',
      skill2Label: 'Skill 2',
      pathwayNotesTitle: 'PATHWAY NOTES',
      pathwayNotesLevelHeader: (level: number) => `LEVEL ${level}: PLAYER PATHWAY`,
      skillCheckTitle: 'Skill Check',
      putYourFormsToWork: 'Put Your Forms to Work',
      instructionForms: 'Before moving to skills, review the forms.',
      instructionLink: (materialLabel: string) => `Please see ${materialLabel}`,
      instructionRevision: 'In Revision',
      incompleteTitle: (stepLabel: string) => `${stepLabel}: Incomplete`,
      incompleteBody: 'Great effort! Would you like to try again?',
      achievedTitle: (levelName: string, skillName: string, isTablet: boolean) =>
        isTablet
          ? `${levelName} ${skillName} Pin Achieved`
          : `${levelName} ${skillName}\nPin Achieved`,
      nextSkill: 'NEXT SKILL',
      levelCompleteTitle: (levelName: string, isTablet: boolean) =>
        isTablet
          ? `Congratulations! ${levelName} Complete!`
          : `Congratulations!\n${levelName} Complete!`,
      nextLevel: 'NEXT LEVEL',
      /** Max level (10): dismiss certificate and return to player overview. */
      levelCompleteBack: 'BACK',
    },
  },

  mutatePlayer: {
    validation: {
      playerHeightMustBeNumber: 'Player height must be a number',
      phoneNumberDigitsRange: 'Phone Number must be at least 10 characters',
      altPhoneNumberDigitsRange: 'Phone Number must be at least 10 characters',
      phoneNumberInvalid: 'Invalid Phone Number format',
      emailInvalid: 'Invalid Email format',
      avatarTooLarge: 'Your image exceeds 10MB. Please upload another image',
      zipCodeMustBeNumeric: 'ZIP Code must contain only digits',
      birthDateRequired: 'Birth date is required',
      birthDateCannotBeFuture: 'Birth date cannot be in the future',
      fieldRequired: 'This field is required',
    },
    footer: {
      noticeTitle: 'Notice',
      errorForm: 'Some required fields do not have correct information or are complete.',
      successMessage: 'Successfully created a new player!',
      dismiss: 'Dismiss',
      cancelMessage: 'Any changes you have made will be lost.',
      confirmDiscard: 'Continue',
      continueEditing: 'Cancel',
    },
  },

  mutateCoach: {
    title: 'Edit Profile',
    profile: {
      phoneNumber: 'Phone Number',
      country: 'Country',
      city: 'City',
      state: 'State/Province',
      zipCode: 'Zip Code',
      editContactInfo: 'EDIT CONTACT INFO',
    },
  },

  splash: {
    mandatoryRestart: {
      title: 'Restart Required',
      message:
        'A new update has just been downloaded. You need to restart the app to apply the new settings.',
      restartNow: 'Restart now',
    },
    loadTimeout: {
      title: 'Taking Longer Than Expected',
      message: 'The app is taking longer than usual to start. Please retry to restart the app.',
      retry: 'Retry',
    },
  },

  coachProfile: {
    header: {
      back: 'BACK',
      title: 'COACH PROFILE',
      edit: 'EDIT',
    },
    content: {
      address: 'ADDRESS',
      phoneNumber: 'Phone Number',
      email: 'Email',
      configInactiveFilter: 'The default inactive filter is',
      updateDefaultInactiveFilter: 'Update Default Inactive Filter',
      done: 'DONE',
    },
  },

  coach: {
    notifications: {
      title: 'Notifications',
      titleWithUnreadCount: (count: number) => `Notifications (${count})`,
      back: 'Back',
      permissionWarning: "Turn on app notifications so you don't miss any important updates.",
      types: {
        classReminders: 'Class Reminders',
        bookingActivities: 'Booking Activities',
        system: 'System',
      },
      settings: {
        notificationPreferences: 'Notification Preferences',
        markAllAsRead: 'Mark all as read',
        close: 'Close settings',
      },
      empty: {
        title: 'No Notification',
        description: 'Lesson reminders, booking notifications and system messages will land here.',
        action: 'Notification Settings',
      },
      accessibility: {
        settings: 'Notification settings',
        delete: (title: string) => `Delete ${title}`,
      },
      time: {
        now: 'Now',
        minute: 'min',
        hour: 'hour',
        day: 'day',
        month: 'month',
        year: 'year',
      },
    },
    notificationPreferences: {
      title: 'Notification Preferences',
      back: 'Back',
      permissionWarning: "Turn on app notifications so you don't miss any important updates.",
      options: {
        classReminders: {
          title: 'Class Reminders',
          description: '1 hour before each lesson',
        },
        bookingActivities: {
          title: 'Booking Activities',
          description: 'Booking-related notifications',
        },
        system: {
          title: 'System',
          description: 'System notification',
        },
      },
    },
  },

  guardian: {
    header: {
      goodMorning: 'Good morning',
      goodAfternoon: 'Good afternoon',
      goodEvening: 'Good evening',
      late: "It's late night",
    },
    notifications: {
      title: 'Notifications',
      titleWithUnreadCount: (count: number) => `Notifications (${count})`,
      permissionWarning: "Turn on app notifications so you don't miss any important updates.",
      filters: {
        all: 'All',
        classReminders: 'Class Reminders',
        pinsAndCoachNotes: 'New Pins & Coach Notes',
        tournamentUpdates: 'Tournament Updates',
        coGuardianActivities: 'Co-Guardians Activities',
      },
      settings: {
        markAllAsRead: 'Mark all as read',
        notificationPreferences: 'Notification Preferences',
      },
      empty: {
        title: 'No Notification',
        description: 'New pins, class reminders, and tournament results will land here.',
        action: 'Notification Settings',
      },
      accessibility: {
        settings: 'Notification settings',
        close: 'Close notifications',
        delete: (title: string) => `Delete ${title}`,
      },
      time: { now: 'Now' },
    },
    profile: {
      title: 'Guardian Profile',
      subtitle: 'Manage your account settings',
      changePassword: 'Change Password',
      signOut: 'Sign out',
    },
    coGuardianInvite: {
      title: "You're invited",
      email: 'patjones@gmail.com',
      description: (inviterName: string, playerName: string) =>
        `${inviterName} wants you to help manage ${playerName}'s golf journey.`,
      accessTitle: 'Your access will include',
      accessItems: (playerName: string) => [
        `See ${playerName}'s sessions, pins & coach notes`,
        'Book or cancel classes (shared packages)',
        'Register for tournaments',
        'Cannot remove other guardians',
      ],
      expiry: (date: string) => `This invitation will be valid until ${date}`,
      accept: 'Accept & Join',
      decline: 'Decline',
      acceptAccessibilityLabel: 'Accept invitation and join',
      declineAccessibilityLabel: 'Decline invitation',
      copyright: '© 2026, U.S. Kids Golf, LLC.',
      result: {
        acceptTitle: 'Invitation Accepted',
        acceptMessage: (playerName: string) =>
          `You can start managing ${playerName} from your list. You can also leave ${playerName}'s circle at any time.`,
        declineTitle: 'Invitation Declined',
        declineMessage:
          "You've declined the invitation. If you change your mind, ask Primary Guardian to send a new invitation.",
        ok: 'Ok',
      },
      error: {
        expiredTitle: 'Expired Invitation',
        invalidTitle: 'Invalid Invitation',
        unknownTitle: 'Something Went Wrong!',
        incorrectAccountTitle: 'Incorrect Account',
        loginRequiredTitle: 'Login Required',
        sentTo: 'This invitation was sent to',
        expiredOn: (date: string) => `This link expired on ${date}`,
        expiredFallback: 'This invitation link has expired.',
        expiredHelp: (inviterName: string, playerName: string) =>
          `Ask ${inviterName} to send the invitation again to start managing ${playerName}’s golf journey.`,
        expiredHelpFallback:
          'Ask the Primary Guardian to send the invitation again to start managing the player’s golf journey.',
        invalidMessage: 'This invitation link is invalid or no longer available.',
        unknownMessage: 'Something went wrong while opening this invitation. Please try again.',
        accountMessage: 'Please log in with this account to accept.',
        accountFallback: 'Please log in with the account that received this invitation to accept.',
        goHome: 'Go to Home',
        logoutAndLogin: 'Log Out & Log In again',
        login: 'Log In',
      },
    },
    home: {
      myKids: 'My Players',
      levelBadge: 'LEVEL {level}',
      lessonCarousel: {
        viewDetails: 'View details',
        reschedule: 'Reschedule',
      },
      upcomingWeek: {
        title: 'Coming Up This Week',
        emptyTitle: 'There are no Upcoming events',
        emptySubtitle: "Book your player's lesson now",
        bookClass: 'Book a Session',
      },
      newNotes: {
        title: 'New Notes',
        viewMore: 'View more',
        showLess: 'Show less',
        coachLabel: 'Coach',
      },
      familyActivity: {
        title: 'Family Activity',
        empty: 'No recent activity',
      },
    },
    kids: {
      myFamily: 'My family',
      ageBadge: 'Age {age}',
      pins: '{earned}/{total} pins',
      next: 'Next: {time}',
      familyAgenda: 'Family agenda',
      calendar: 'Calendar',
      noFamilyAgendaEvents: 'No upcoming events',
      noEventsScheduled: 'There are no events scheduled',
      previousMonth: 'Previous month',
      nextMonth: 'Next month',
      selectDate: 'Select {date}',
      moreEvents: '+{count} events',
      // ── Family Calendar screen (PP-50x) ──────────────────────────────────
      calendarTitle: 'Calendar',
      calendarViewLabel: 'Calendar view',
      listViewLabel: 'List view',
      showPlayer: 'Show {name}',
      showAllPlayers: 'Show all players',
      noUpcomingEvents: 'There are no upcoming events',
      eventAlertMessage: '{title} · {player}\n{date} at {time}',
      // ── Daily View (weekly timeline, BR-04/BR-07) ────────────────────────
      eventCount: (count: number) => `${count} ${count === 1 ? 'event' : 'events'}`,
      dayEventsTitle: (count: number) => `${count} ${count === 1 ? 'Event' : 'Events'}`,
      moreDayEvents: (count: number) => `+${count}\n${count === 1 ? 'Event' : 'Events'}`,
      viewMoreDayEvents: (count: number) =>
        `View ${count} more ${count === 1 ? 'event' : 'events'}`,
      closeDayEvents: 'Close day events',
      currentTime: 'Current time',
      eventDetail: {
        screenTitle: 'Event details',
        close: 'Close',
        more: 'More options',
        share: 'Share',
        guestCount: (count: number) => `${count} ${count === 1 ? 'guest' : 'guests'}`,
        ageBadge: 'Age {age}',
        coachLead: 'The coach is',
        viewMap: 'View map',
        mapAlertTitle: 'Map',
        mapAlertMessage: 'Map is coming soon.',
        distanceAway: '{distance} miles away',
        bookedBy: 'Booked by {name}',
        registeredBy: 'Registered by {name}',
        bookedAgo: (name: string, ago: string) => `${name} • ${ago}`,
        statusBooked: 'Booked',
        statusRegistered: 'Registered',
        statusCancelled: 'Cancelled',
        reschedule: 'Reschedule',
        viewBooking: 'View booking',
        notFound: 'This event is no longer available',
      },
    },
    playerProgressTracker: {
      fallbackPlayerName: 'Player',
      screenTitle: "{name}'s Path",
      currentLevel: 'Currently on Level {level}',
      percentThrough: '{percent}% through',
      academicProgress: "{name}'s academic progress",
      levelTitle: 'Level {level}',
      levelAccessibilityLabel: 'Level {level}',
      badgeCount: '{earned}/{total}',
      earned: 'Earned',
      earnedDate: 'on {date}',
      inProgress: 'In Progress',
      unlockAfterLevel: 'Unlock after Level {level}',
      viewFullSessionHistory: 'View full session history',
    },
    playerSessionHistories: {
      fallbackPlayerName: 'Player',
      screenTitle: "{name}'s Session",
      sessionsSummary: (count: number) => `${count} ${count === 1 ? 'Session' : 'Sessions'}`,
      pinsEarnedSummary: (count: number) => `${count} ${count === 1 ? 'Pin' : 'Pins'} Earned`,
      since: 'Since {date}',
      pinBadge: '+{pins} {label}',
      pinSingular: 'pin',
      pinPlural: 'pins',
      emptyTitle: 'No sessions yet',
      emptyDescription: 'Completed practice and competition sessions will appear here.',
      sessionDetailTitle: 'Session Detail',
      sessionDetailMessage: 'Open session {session} detail',
    },
    playerSessionDetails: {
      screenTitle: 'Session Details',
      skillLearned: 'SKILL LEARNED',
      emptyTitle: 'No skills were recorded for this session',
      levelTitle: 'Level {level}',
      badgeStatus: {
        earned: 'Earned',
        working: 'Working on it',
        notStarted: 'Not started yet',
      },
      checkpointStatus: {
        completed: 'Completed',
        incomplete: 'Incomplete',
      },
    },
    changePassword: {
      title: 'Change Password',
      minimumHint: 'At least 8 Characters',
      currentPasswordLabel: 'Current Password',
      newPasswordLabel: 'New Password',
      confirmPasswordLabel: 'Confirm Password',
      confirmNewPasswordLabel: 'Confirm Password',
      back: 'Back',
      save: 'Save',
      saveChange: 'Save change',
      errorCurrentPassword: 'Current password is incorrect.',
      validationMinLength: 'Password must be at least 8 characters.',
      validationConfirmMismatch: 'Confirm Password do not match.',
    },
    support: {
      title: 'Help & Support',
      safetyConcern: 'Reporting a safety concern about a coach or guardian? ',
      contactUs: 'Contact us.',
      contactActionMessage:
        'This action will help you contact support about a safety concern involving a coach or guardian.',
      articles: {
        playerPathwayOverview: {
          title: 'Player Pathway Overview',
          intro:
            'To make learning the key fundamentals of the game fun, U.S. Kids Golf has developed the Player Pathway, consisting of ten levels.',
          checkpointLead: 'Within each level, there are five checkpoints to earn ',
          achievementPinsLabel: 'Achievement Pins: ',
          checkpointNames: 'Putting, Around the Green, Full Swing, Scoring, and Knowledge.',
          masteredPrefix: ' Once all five are attained, a ',
          masteredPin: 'Mastered Pin',
          masteredSuffix: ' is awarded, and the player advances to the next level.',
        },
        playerPathwayMethod: {
          title: 'The Player Pathway Method',
          subtitle: 'Playing Games',
          sections: {
            playingGames: {
              title: 'Playing Games',
              body: 'Young players stay motivated by playing different games at each checkpoint.',
            },
            developingForms: {
              title: 'Developing Forms',
              body: 'Coaches guide players to discover good Forms as games are played.',
            },
            assessingSkills: {
              title: 'Assessing Skills',
              body: 'Players earn Achievement Pins when the required skill is completed twice in each of the checkpoints using good Forms.',
            },
          },
        },
      },
    },
    contactUs: {
      title: 'Contact Us',
      heading: 'Do you have any questions?',
      nameLabel: 'Name',
      namePlaceholder: 'Enter Your Name',
      emailLabel: 'Email',
      questionTypeLabel: 'Question Type',
      questionTypePlaceholder: 'Select a question type',
      questionTypeRequired: 'Please select a Concern Type',
      messageLabel: 'Message',
      messagePlaceholder: 'Type your message here...',
      submit: 'Submit',
      cancel: 'Cancel',
      ok: 'OK',
      successTitle: 'Notice',
      successMessage:
        'Thanks for reaching out! Your message has been sent and we will get back to you soon.',
      questionTypes: {
        product: 'Product',
        coaching: 'Coaching',
        tournament: 'Tournament',
      },
    },
    reportIssue: {
      title: 'Report Issue',
      firstNameLabel: 'First Name',
      lastNameLabel: 'Last Name',
      emailLabel: 'Email',
      issueLabel: 'Issue',
      issuePlaceholder: 'Type the issue title here…',
      uploadLabel: 'Evidence',
      uploadLabelWithLimit: 'Evidence (up to 5 images and 1 video)',
      uploadPlaceholder: 'Upload images / video',
      uploadSelected: (count: number) => `${count} file${count > 1 ? 's' : ''} selected`,
      uploadSizeErrorImage:
        'Image exceeds the maximum file size of 10MB. Please upload a smaller file.',
      uploadSizeErrorVideo:
        'Video exceeds the maximum file size of 50MB. Please upload a smaller file.',
      uploadSizeErrorBoth: 'Please upload a video smaller than 50MB and images smaller than 10MB.',
      uploadLimitMessage: 'You can only upload a maximum of 5 images and 1 video.',
      fieldRequired: 'This field is required.',
      removeAttachment: 'Remove attachment',
      previewAttachment: 'Preview attachment',
      closePreview: 'Close preview',
      videoBadge: 'Video',
      descriptionLabel: 'Description',
      descriptionPlaceholder: 'Type the description here...',
      descriptionMaxLength: 'Description must be 500 characters or fewer.',
      submit: 'Submit',
      ok: 'OK',
      successTitle: 'Notice',
      successMessage:
        'Thanks for the report! Our team has received it and will look into the issue.',
    },
  },

  guardianProfile: {
    title: 'Profile',
    fallbackName: 'Guardian',
    coGuardians: {
      phoneTitle: 'Co-Guardians',
      tabletTitle: 'Co-guardians',
      mutual: (count: number) => `${count} mutual`,
    },
    productOrders: 'Product Orders',
    myOrders: 'My Orders',
    myOrder: 'My Order',
    settings: {
      title: 'Settings',
      account: 'Account',
      security: 'Account Securities',
      notifications: 'Notification Preferences',
      support: 'Help & Support',
      legal: 'Privacy & Legal',
      reportIssue: 'Report Issue',
    },
    notificationPreferences: {
      title: 'Notification Preferences',
      tabletTitle: 'Notifications',
      subtitle: 'Pick which player events ping you. Co-guardians set their own.',
      save: 'Save',
      warning: "Turn on app notifications so you don't miss any important updates.",
      rows: {
        classReminders: {
          title: 'Class Reminders',
          subtitle: '1 hour before each lesson',
        },
        coachNotes: {
          title: 'New Pins & Coach Notes',
          subtitle: 'After every session',
        },
        tournamentUpdates: {
          title: 'Tournament Updates',
          subtitle: 'Reminders, waitlist, results',
        },
        coGuardianActivity: {
          title: 'Co-guardian Activities',
          subtitle: 'When other guardians book',
        },
      },
    },
    accountSecurities: {
      title: 'Account Securities',
      sections: {
        security: 'Security',
        biometric: 'Biometric',
        socialAccounts: 'Social Accounts',
      },
      changePassword: 'Change Password',
      biometricTitle: 'Login with ${kind}',
      biometricSubtitle: 'Use ${kind} to log in instead of a password.',
      appleAccount: 'Apple Account',
      appleSubtitle: 'You are connected with Apple ID',
      googleAccount: 'Google Account',
      googleSubtitle: 'You are connected with Google',
      connected: 'Connected',
      notConnected: 'Not Connected',
    },
    version: {
      phone: (version: string) => `Version ${version}`,
      tablet: (version: string) => `Version ${version}`,
    },
    logout: {
      phone: 'Log Out',
      tablet: 'Log out',
      accessibilityLabel: 'Log out',
      alertTitle: 'Confirm Logout',
      alertMessage: 'Are you sure you want to log out of your account?',
      confirm: 'Logout',
      cancel: 'Cancel',
    },
    placeholder: (title: string) => `${title} screen`,
    edit: {
      uploadPhoto: 'UPLOAD PHOTO',
      photoHint: 'Image should not exceed 10MB',
      firstName: 'First Name',
      lastName: 'Last Name',
      phone: 'Phone Number',
      email: 'Email',
      country: 'Country',
      state: 'State/Province',
      city: 'City',
      zipCode: 'Zip Code',
      save: 'Save',
      saveChange: 'Save change',
      deleteAccount: 'Delete My Account',
      required: 'This field is required.',
      updateSuccess: 'Your profile has been updated successfully.',
      avatarTooLarge: 'Your image exceeds 10MB. Please upload another image.',
      namePreview: (firstName: string, lastName: string) =>
        `${firstName} ${lastName}`.trim() || 'Guardian',
    },
    deleteAccount: {
      title: 'Delete Account',
      warning:
        'Your account is scheduled for permanent deletion in 30 days. Until then, your account is on hold and all existing data remains intact.\nChanged your mind? Log back in anytime before the deadline to cancel your deletion request.',
      reasonLabel: 'Reason',
      reasonPlaceholder: 'Help us improve...',
      reasonMaxLength: 200,
      acknowledgement: "I understand this can't be undone after 30 days.",
      passwordLabel: 'Confirm your password',
      passwordPlaceholder: 'Enter your password',
      submit: 'Permanently Delete',
      errorIncorrectPassword: 'Incorrect password. Please try again.',
      errorSessionExpired: 'Your session has expired. Please log in again.',
      errorGeneric: 'Something went wrong. Please try again.',
      successConfirm: 'OK',
      successMessage: (when: string) =>
        `Your account will be deleted after ${when}.\nLog in at any time before then to keep your account active.`,
    },
  },

  guardianPlayer: {
    levelDetail: {
      back: 'Back',
      more: '...',
      levelTitle: (level: number) => `Level ${level}`,
      emptyProgress: 'No level progress found',
      progressThrough: (percent: number) => `${percent}% through`,
      progressThroughSuffix: 'through',
      levelStatus: {
        completed: 'Completed',
        inProgress: 'In Progress',
      },
      pinStatus: {
        earned: 'Earned',
        working: 'Working on it',
        notStarted: 'Not started yet',
      },
      panel: {
        whatItTests: 'WHAT IT TESTS',
        criteria: 'CRITERIA',
        attempts: 'ATTEMPTS',
        noAttempts: 'No attempts yet',
        completedOn: (date: string) => `Completed on ${date}`,
        incompleteAttemptOn: (date: string | undefined) => `Incomplete Attempt on ${date}`,
        viewCoachMaterial: 'View coach material for this pin',
      },
    },
    coachMaterials: {
      title: 'Coach materials',
      description:
        'The references coaches use to assess each level. Useful if you want to know "why this pin isn\'t yet earned".',
      levelOneToThree: 'Level 1-3: Beginner',
      levelFourToFive: 'Level 4-5: Intermediate',
      levelOneToThreeDescription: {
        phone: 'The Beginner Stage, Levels 1–3, are included in this booklet.',
        tablet:
          'To make learning the key fundamentals of the game fun, U.S. Kids Golf has developed the Player Pathway, consisting of ten levels. The Beginner Stage, Levels 1–3, are included in this booklet.',
      },
      levelFourToFiveDescription: {
        phone:
          'Once completing the initial, Beginner state, players are introduced to The Intermediate Stage, Levels 4-5.',
        tablet:
          'Once completing the initial, Beginner state, players are introduced to The Intermediate Stage, Levels 4-5. Increased focus on the basics of golf with more challenges throughout the levels. A player will receive a Gold Star Player Medal after completing Level 5!',
      },
    },
    playerDetail: {
      fallbackPlayerName: 'Player',
      screenTitle: "{name}'s Path",
      stats: {
        sessions: 'Sessions',
        packageLeft: 'Pkg left',
        upcoming: 'Upcoming',
        guardians: 'Guardian(s)',
      },
      cards: {
        progress: 'Progress',
        notes: 'Notes',
        notesCount: (count: number) => `${count} ${count === 1 ? 'note' : 'notes'}`,
        guardians: 'Guardians',
        package: 'Package',
      },
      lessons: {
        title: "{name}'s Lessons",
        nextUp: 'Next up:',
        viewDetail: 'View detail',
        reschedule: 'Reschedule',
        emptyTitle: 'There are no Upcoming events',
        emptySubtitle: "Book your player's lesson now",
        bookClass: 'Book a Session',
      },
    },
    guardianCircle: {
      fallbackPlayerName: 'Player',
      screenTitle: "{name}'s Circle",
      subtitle: "{count} adults can see {name}'s progress, book lessons, and get notifications.",
      subtitleSolo: "Only you can see {name}'s progress, book lessons, and get notifications.",
      you: '(You)',
      invite: 'Invite another person',
      emptyTitle: 'No other guardians yet',
      emptyMessage:
        "{name}'s circle is just you for now. Invite a co-parent, grandparent, or coach so they can follow every session and milestone too.",
      emptyInvite: 'Invite Guardian',
      createdProfile: 'Created profile',
      metaCreated: '· Created profile since {date}',
      metaSince: '· Joined since {date}',
      metaInvited: '· Invited {time}',
      relationships: {
        parent: 'Parent',
        grandparent: 'Grandparent',
        coach: 'Coach',
        guardian: 'Guardian',
        other: 'Other',
      },
      leave: "Leave {name}'s Circle",
      leaveConfirmTitle: 'Leave circle?',
      leaveConfirmMessage:
        "You'll no longer be able to see {name}'s progress or get notifications.",
      leaveConfirm: 'Leave',
      cancel: 'Cancel',
      status: {
        waiting: 'Waiting',
        expired: 'Expired',
        declined: 'Declined',
      },
    },
    coGuardianProfile: {
      fallbackName: 'Guardian',
      fallbackPlayerName: 'Player',
      screenTitle: "{name}'s Co-Guardian",
      joinedSince: 'Joined since {date}',
      createdProfileSince: 'Created profile since {date}',
      mutualKidsTitle: 'Mutual Players',
      recentActivitiesTitle: 'Recent Activities',
      remove: 'Remove',
      emptyActivities: 'No recent activity',
      emptyMutualKids: 'No mutual kids yet',
      kidMeta: 'Age {age} · Level {level}',
      kidLevelOnly: 'Level {level}',
      kidSince: 'Since {date}',
      relationships: {
        parent: 'Parent',
        grandparent: 'Grandparent',
        coach: 'Coach',
        guardian: 'Guardian',
        owner: 'Guardian',
        member: 'Co-Guardian',
        coguardian: 'Co-Guardian',
        other: 'Other',
      },
      detailSheet: {
        title: 'Profile Detail',
        close: 'Close',
        ageYearOld: 'year old',
        ageYearsOld: 'years old',
        birthdate: 'Birthdate:',
        gender: 'Gender:',
        height: 'Height:',
        dominantHand: 'Dominant Hand:',
        address: 'Address:',
        zipCode: 'Zip code:',
      },
    },
    invitationSent: {
      fallbackPlayerName: 'Player',
      screenTitle: 'Invitation Sent',
      canJoin: "can join {name}'s circle.",
      sharingTitle: 'Co-guardian sharing',
      statusLabel: 'Status',
      expiresLabel: 'Expires',
      relationshipLabel: 'Relationship',
      /** "10 days left · May 28, 2026" while an invite is still pending. */
      expiresCountdown: (days: number, date: string) =>
        `${days} ${days === 1 ? 'day' : 'days'} left · ${date}`,
      waitingHint: "You'll get a notification when they accept, decline, or it expires.",
      revoke: 'Revoke invitation',
      shareLink: 'Share Link',
      resend: 'Resend',
      remove: 'Remove',
      removeInvitation: 'Remove Invitation',
      backToCircle: "Back to {name}'s circle",
      shareMessage:
        "Join {name}'s circle on U.S. Kids Golf so you can follow every session and milestone.",
      revokeConfirmTitle: 'Revoke invitation',
      revokeConfirmMessage:
        'Are you sure you want to revoke this invitation? The recipient will no longer be able to join the circle.',
      revokeConfirm: 'Revoke',
      revokeCancel: 'Cancel',
      revokeErrorTitle: 'Notice',
      revokeErrorMessage: 'Something went wrong revoking the invitation. Please try again.',
      resendConfirmTitle: 'Resend invitation',
      resendConfirmMessage:
        'Are you sure you want to resend this invitation? The recipient will receive a new invitation email.',
      resendConfirm: 'Resend',
      resendCancel: 'Cancel',
      resendErrorTitle: 'Notice',
      resendErrorMessage: 'Something went wrong resending the invitation. Please try again.',
      removeErrorTitle: 'Notice',
      removeErrorMessage: 'Something went wrong removing the invitation. Please try again.',
    },
    leaveCircle: {
      fallbackPlayerName: 'Player',
      screenTitle: "Leave {name}'s Circle",
      warning:
        "You'll stop seeing {name}'s data & notifications. {name} isn't deleted - other guardians keep access.",
      cardDetail: 'Age {age} · Level {level}',
      cardDetailNoAge: 'Level {level}',
      cardSince: 'Since {date}',
      staysTitle: 'Stays with you',
      staysAccount: 'Your account information',
      staysPlayers: 'Your other players',
      loseTitle: "You'll lose",
      loseProgress: 'Progress, sessions & coach notes for {name}',
      loseBookings: 'Upcoming bookings for {name}',
      loseNotifications: "{name}'s notifications from other guardians",
      leave: 'Leave',
    },
    removeCoGuardian: {
      fallbackName: 'Co-guardian',
      fallbackPlayerName: 'Player',
      screenTitle: 'Remove Co-Guardian',
      joinedSince: 'Joined since {date}',
      cardDetail: 'Age {age} · Level {level}',
      cardDetailNoAge: 'Level {level}',
      cardSince: 'Since {date}',
      effectTitle: 'Effect',
      effectLosesAccess: "{name} loses all {player}'s access immediately",
      effectBookingsStay: "{name}'s upcoming bookings stay (you can cancel)",
      reasonLabel: 'Reason',
      reasonRequiredSuffix: ' (required)',
      reasons: {
        noLongerCoParenting: 'No longer co-parenting',
        safetyConcern: 'Safety concern',
        other: 'Other',
      },
      detailPlaceholder: 'Add detail (visible to {name})...',
      acknowledgement: 'I understand removal is logged & {name} will be notified.',
      warning:
        'This is a sensitive action. Try inviting {name} to leave on their own. The removal action will be logged.',
      remove: 'Remove {name}',
    },
    inviteGuardian: {
      fallbackPlayerName: 'Player',
      screenTitle: 'Invite to {name}',
      relationshipLabel: 'Relationship',
      roles: {
        parent: 'Parent',
        grandparent: 'Grandparent',
        guardian: 'Guardian',
        other: 'Other',
      },
      emailLabel: 'Email',
      emailPlaceholder: 'Enter email address',
      messagePlaceholder: 'Write a message...',
      infoTitle: 'Your invite will include',
      infoDetailWithAge: 'Age {age} · Level {level}',
      infoDetail: 'Level {level}',
      infoSince: 'Since {date}',
      benefitSessions: "See {name}'s sessions, pins & coach notes",
      benefitBooking: 'Book or cancel classes (shared packages)',
      benefitTournaments: 'Register for tournaments',
      benefitBilling: 'Pay billing is shared',
      benefitCannotRemove: 'Cannot remove other guardians',
      submit: 'Invite',
      successMessage: 'Invitation sent',
      /** Shown when the invited email has no Guardian account (BE: InvitedAccountNotEligibleError). */
      errorNotEligible: "We couldn't find a Guardian account with this email.",
      /** Shown when the guardian is already in the circle (BE: AlreadyCoGuardianError). */
      errorAlreadyInCircle: 'This Guardian is already in the circle.',
    },
    kitNotes: {
      fallbackPlayerName: 'Player',
      screenTitle: (name: string) => `${name}'s KIT & Notes`,
      tabs: {
        coachNotes: 'Coach notes',
        kitClubs: 'KIT / Clubs',
      },
      emptyNotes: 'There are no notes',
      emptyKit: 'There are no KIT / Clubs',
      kitListTitle: 'Kits Provided by date:',
      kitLevelLabel: (level: number) => `LEVEL ${level}`,
      kitProvidedLine: (date: string, name: string) => `Provided on ${date} by ${name}`,
    },
  },
} as const;
